import React from "react"
import { motion } from "framer-motion"
import { useTheme } from "../contexts/ThemeContext"

const TimeVisual = () => {
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === "dark"
  const trackBg = isDark ? "#222a3a" : "#f1f5f9"
  const labelColor = isDark ? "#8a93a6" : "#94a3b8"
  const valueColor = isDark ? "#eef0f5" : "#1D3047"
  const valueAfter = isDark ? "#93b3ff" : "#2D5F9E"
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: labelColor }}>Before</span>
            <span style={{ fontSize: 10, fontWeight: 700, color: valueColor }}>24 min</span>
          </div>
          <div style={{ background: trackBg, borderRadius: 6, height: 7, overflow: "hidden" }}>
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: "78%" }}
              transition={{ duration: 1, delay: 0.3 }}
              viewport={{ once: true }}
              style={{ height: "100%", borderRadius: 6, background: "linear-gradient(90deg,#1D3047,#2D5F9E)" }}
            />
          </div>
        </div>
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: labelColor }}>After</span>
            <span style={{ fontSize: 10, fontWeight: 700, color: valueAfter }}>16 min</span>
          </div>
          <div style={{ background: trackBg, borderRadius: 6, height: 7, overflow: "hidden" }}>
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: "50%" }}
              transition={{ duration: 1, delay: 0.5 }}
              viewport={{ once: true }}
              style={{ height: "100%", borderRadius: 6, background: "linear-gradient(90deg,#E3B02E,#f0c040)" }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

const RoutingVisual = () => {
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === "dark"
  const inactiveDot   = isDark ? "#2a3346" : "#e2e8f0"
  const inactiveBdr   = isDark ? "#3a4358" : "#cbd5e1"
  const activeRing    = isDark ? "#13161e" : "#fff"
  const labelInactive = isDark ? "#8a93a6" : "#94a3b8"
  return (
    <div style={{ marginBottom: 14, padding: "8px 0" }}>
      <div style={{ display: "flex", alignItems: "center" }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <React.Fragment key={i}>
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.12 * i, duration: 0.3 }}
              viewport={{ once: true }}
              style={{
                width: i === 2 ? 18 : 11,
                height: i === 2 ? 18 : 11,
                borderRadius: "50%",
                flexShrink: 0,
                background: i === 2 ? "#E3B02E" : i < 2 ? "#2D5F9E" : inactiveDot,
                border: i === 2 ? `3px solid ${activeRing}` : i < 2 ? "2px solid #2D5F9E" : `2px solid ${inactiveBdr}`,
                boxShadow: i === 2 ? "0 0 0 3px rgba(227,176,46,0.25)" : "none",
                zIndex: 2,
              }}
            />
            {i < 4 && <div style={{ flex: 1, height: 2, background: i < 2 ? "#2D5F9E" : inactiveDot }} />}
          </React.Fragment>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5 }}>
        {["Stop A", "Library", "◉ Now", "Gate", "Hub"].map((l, i) => (
          <span key={i} style={{
            fontSize: 8.5, color: i === 2 ? "#E3B02E" : labelInactive,
            fontWeight: i === 2 ? 700 : 500, textAlign: "center",
          }}>{l}</span>
        ))}
      </div>
    </div>
  )
}

const TrackingVisual = () => (
  <div style={{ marginBottom: 14 }}>
    <div style={{
      display: "flex", alignItems: "center", gap: 9,
      background: "#F8FAFF", border: "1px solid #e2e8f0",
      borderRadius: 9, padding: "8px 11px",
    }}>
      <div style={{
        width: 30, height: 30, borderRadius: 7, flexShrink: 0,
        background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2">
          <rect x="1" y="3" width="15" height="13" rx="2"/>
          <path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
        </svg>
      </div>
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: "#1D3047", lineHeight: 1.2 }}>Bus 002 · Eng. Loop</p>
        <p style={{ fontSize: 9, color: "#64748b" }}>33.6844° N · 73.0479° E</p>
      </div>
      <span style={{
        fontSize: 9, fontWeight: 700, color: "#16a34a",
        background: "#dcfce7", borderRadius: 5, padding: "2px 6px", whiteSpace: "nowrap",
      }}>±0.8 m</span>
    </div>
  </div>
)

const cards = [
  {
    num: "01", cat: "TIME", label: "SAVE TIME",
    iconBg: "#EEF4FF", iconColor: "#2D5F9E",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
      </svg>
    ),
    headline: "Cut wait time by a third.",
    body: "Adaptive scheduling fills gaps between classes and trims dead time at the stop — so you spend less of your day standing around.",
    visual: <TimeVisual />,
    stat: "35%", statSub: "LESS TIME\nSPENT WAITING",
  },
  {
    num: "02", cat: "ROUTING", label: "SMART ROUTING",
    iconBg: "#EEF4FF", iconColor: "#2D5F9E",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
      </svg>
    ),
    headline: "AI that dodges delays.",
    body: "Live traffic, weather and demand signals reroute buses around bottlenecks before they form — your driver just follows the plan.",
    visual: <RoutingVisual />,
    stat: "2.4M", statSub: "TRIPS ROUTED\nTHIS SEMESTER",
  },
  {
    num: "03", cat: "TRACKING", label: "LIVE TRACKING",
    iconBg: "#EEF4FF", iconColor: "#2D5F9E",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
      </svg>
    ),
    headline: "Know exactly when it's here.",
    body: "Sub-meter GPS streams every bus's position to your phone in real time, with arrival countdowns precise to the second.",
    visual: <TrackingVisual />,
    stat: "1.2s", statSub: "AVERAGE\nUPDATE LATENCY",
  },
]

const HeroFeatures: React.FC = () => {
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === "dark"

  /* Headline + body text colors flip in dark; accent stays gold. */
  const headingColor = isDark ? "#eef0f5" : "#1D3047"
  const bodyColor    = isDark ? "#9aa3b6" : "#64748b"
  const labelColor   = isDark ? "#8a93a6" : "#94a3b8"
  const dimColor     = isDark ? "#6b7488" : "#cbd5e1"
  const cardBorder   = isDark ? "1px solid #1b2230" : "1px solid #f1f5f9"
  const iconBg       = isDark ? "rgba(63,94,174,0.22)" : "#EEF4FF"

  return (
    <section
      className="relative z-30 px-4 sm:px-6 lg:px-16 xl:px-24 pt-0 pb-16 md:pb-20 overflow-hidden"
      style={{
        /* In dark mode use a solid lighter ink tone so all landing
           sections share the same elevated panel feel; the wave divider
           above matches with no visible seam. */
        background: isDark
          ? "#13161e"
          : "linear-gradient(135deg,#EEF2FF 0%,#F5F8FF 40%,#FFFDF5 75%,#FFFBEE 100%)",
      }}
    >
      <div className="max-w-7xl mx-auto">

        {/* header */}
        <div className="flex flex-col lg:flex-row lg:items-end gap-5 lg:gap-16 mb-10 md:mb-12">
          <motion.div
            className="lg:flex-1"
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 mb-4">
              <span style={{ fontSize: 11, fontWeight: 700, color: "#E3B02E", letterSpacing: "0.13em" }}>03</span>
              <div style={{ width: 26, height: 1.5, background: "#E3B02E", borderRadius: 2 }} />
              <span style={{ fontSize: 10, fontWeight: 700, color: labelColor, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                WHY RIDERS CHOOSE SAFERSAVVY
              </span>
            </div>
            <h2
              className="font-black leading-tight tracking-tight"
              style={{ fontSize: "clamp(26px, 3.5vw, 42px)", color: headingColor }}
            >
              Three reasons your campus<br className="hidden sm:block" />
              {" "}commute{" "}
              <span style={{ color: "#E3B02E" }}>just got better.</span>
            </h2>
          </motion.div>

          <motion.p
            className="text-sm sm:text-[14px] leading-relaxed lg:max-w-[280px] xl:max-w-xs"
            style={{ color: bodyColor }}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            viewport={{ once: true }}
          >
            Built for the realities of student life — every feature is measured, tuned, and quietly working in the background so you arrive on time, every time.
          </motion.p>
        </div>

        {/* cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {cards.map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.11 }}
              viewport={{ once: true }}
              className={`flex flex-col rounded-2xl border ${
                isDark ? "bg-ink-800 border-ink-700" : "bg-white border-slate-200/80"
              }`}
              style={{
                padding: "18px 20px 16px",
                boxShadow: isDark
                  ? "0 4px 24px rgba(0,0,0,0.45), 0 1px 4px rgba(0,0,0,0.2)"
                  : "0 4px 24px rgba(29,48,71,0.07), 0 1px 4px rgba(0,0,0,0.03)",
              }}
            >
              {/* top row */}
              <div className="flex items-center justify-between mb-4">
                <div
                  className="flex items-center justify-center rounded-[10px] flex-shrink-0"
                  style={{
                    width: 34,
                    height: 34,
                    background: isDark ? iconBg : c.iconBg,
                    color: isDark ? "#93b3ff" : c.iconColor,
                  }}
                >
                  {c.icon}
                </div>
                <div className="text-right">
                  <p style={{ fontSize: 8.5, fontWeight: 600, color: dimColor, letterSpacing: "0.1em" }}>
                    {c.num} / {c.cat}
                  </p>
                  <p style={{ fontSize: 9.5, fontWeight: 800, color: labelColor, letterSpacing: "0.11em" }}>
                    {c.label}
                  </p>
                </div>
              </div>

              {/* headline */}
              <h3
                className="font-extrabold leading-snug mb-2"
                style={{ fontSize: "clamp(14px, 1.4vw, 16px)", color: headingColor }}
              >
                {c.headline}
              </h3>

              {/* body */}
              <p className="text-[12px] sm:text-[12.5px] leading-relaxed mb-3" style={{ color: bodyColor }}>
                {c.body}
              </p>

              {/* visual */}
              {c.visual}

              {/* stat footer */}
              <div
                className="flex items-end justify-between mt-auto pt-3"
                style={{ borderTop: cardBorder }}
              >
                <span
                  className="font-black tracking-tight leading-none"
                  style={{ fontSize: "clamp(24px, 2.5vw, 32px)", color: headingColor }}
                >
                  {c.stat}
                </span>
                <p
                  className="text-right"
                  style={{
                    fontSize: 8.5, fontWeight: 700, color: labelColor,
                    letterSpacing: "0.08em", textTransform: "uppercase",
                    whiteSpace: "pre-line", lineHeight: 1.5,
                  }}
                >
                  {c.statSub}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  )
}

export default HeroFeatures
